import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { COUNTRY_CODES, searchCountryCodes, CountryCode } from '../utils/contactValidation';

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const SelectButton = styled.button<{ $isOpen?: boolean; $hasError?: boolean }>`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${props => props.$hasError ? '#ef4444' : props.$isOpen ? '#3b82f6' : '#d1d5db'};
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: left;
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#ef4444' : '#3b82f6'};
    box-shadow: 0 0 0 3px ${props => props.$hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  }
  
  &:hover:not(:disabled) {
    border-color: ${props => props.$hasError ? '#ef4444' : '#3b82f6'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #f9fafb;
  }
`;

const SelectedValue = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  color: #1e293b;
`;

const ChevronIcon = styled.span<{ $isOpen: boolean }>`
  font-size: 12px;
  transition: transform 0.2s;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  color: #64748b;
`;

const Dropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  z-index: 100;
  margin-top: 4px;
  max-height: 280px;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  overflow: hidden;
`;

const SearchContainer = styled.div`
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const OptionsList = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const Option = styled.button<{ $isSelected?: boolean }>`
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: ${props => props.$isSelected ? '#eff6ff' : 'transparent'};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  transition: background 0.15s;
  
  &:hover {
    background: ${props => props.$isSelected ? '#dbeafe' : '#f8fafc'};
  }
  
  &:focus {
    outline: none;
    background: #eff6ff;
  }
`;

const Flag = styled.span`
  font-size: 18px;
  line-height: 1;
`;

const CountryInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CountryName = styled.div`
  font-size: 14px;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DialCode = styled.span`
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
  white-space: nowrap;
`;

const NoResults = styled.div`
  padding: 16px;
  text-align: center;
  color: #64748b;
  font-size: 14px;
`;

interface CountryCodeSelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  className?: string;
  placeholder?: string;
}

export const CountryCodeSelect: React.FC<CountryCodeSelectProps> = ({
  value,
  onChange,
  disabled = false,
  hasError = false,
  className,
  placeholder = 'Select country',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Find selected country
  const selectedCountry = COUNTRY_CODES.find(c => c.dialCode === value);
  
  // Filter countries based on search
  const filteredCountries = searchQuery 
    ? searchCountryCodes(searchQuery) 
    : COUNTRY_CODES;
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);
  
  const handleSelect = (country: CountryCode) => {
    onChange(country.dialCode);
    setIsOpen(false);
    setSearchQuery('');
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };
  
  return (
    <Container ref={containerRef} className={className} onKeyDown={handleKeyDown}>
      <SelectButton
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        $isOpen={isOpen}
        $hasError={hasError}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <SelectedValue>
          {selectedCountry ? (
            <>
              <Flag>{selectedCountry.flag}</Flag>
              {selectedCountry.dialCode}
            </>
          ) : (
            <span style={{ color: '#9ca3af' }}>{placeholder}</span>
          )}
        </SelectedValue>
        <ChevronIcon $isOpen={isOpen}>▼</ChevronIcon>
      </SelectButton>
      
      <Dropdown $isOpen={isOpen} role="listbox">
        <SearchContainer>
          <SearchInput
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setSearchQuery(e.target.value)}
            placeholder="Search country or code..."
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          />
        </SearchContainer>
        
        <OptionsList>
          {filteredCountries.length === 0 ? (
            <NoResults>No countries found</NoResults>
          ) : (
            filteredCountries.map(country => (
              <Option
                key={country.code}
                type="button"
                onClick={() => handleSelect(country)}
                $isSelected={country.dialCode === value}
                role="option"
                aria-selected={country.dialCode === value}
              >
                <Flag>{country.flag}</Flag>
                <CountryInfo>
                  <CountryName>{country.name}</CountryName>
                </CountryInfo>
                <DialCode>{country.dialCode}</DialCode>
              </Option>
            ))
          )}
        </OptionsList>
      </Dropdown>
    </Container>
  );
};

export default CountryCodeSelect;
